import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { NgFor, NgIf } from '@angular/common';

import { StorefrontService } from '../../core/services/storefront.service';
import { IconComponent } from './icon.component';
import { PriceComponent } from './price.component';
import { TranslatePipe } from '../pipes/translate.pipe';

/** The minimum an item card needs — menu cards and home collection items both fit. */
export interface CardItem {
  id: string;
  name: string;
  description: string | null;
  image: string | null;
  price: number;
  flags?: string[];
}

/** Desktop grid card: photo, flags, name, description, price and an Add button. */
@Component({
  selector: 'app-desk-item-card',
  standalone: true,
  imports: [NgIf, NgFor, IconComponent, PriceComponent, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [`
    :host { display: block; }
    .card { height: 100%; border: 1px solid var(--fr-border); border-radius: 18px; overflow: hidden; background: var(--fr-surface);
      cursor: pointer; display: flex; flex-direction: column; transition: transform .15s, box-shadow .15s; box-shadow: var(--fr-shadow-sm); }
    .card:hover { transform: translateY(-3px); box-shadow: 0 14px 30px -12px rgba(16,12,8,0.22); }
    .media { position: relative; height: 172px; background: var(--fr-surface-3); overflow: hidden; }
    .media img { width: 100%; height: 100%; object-fit: cover; display: block; }
    .media img.logo { object-fit: fill; box-sizing: border-box; }
    .flags { position: absolute; top: 11px; inset-inline-start: 11px; display: flex; gap: 6px; flex-wrap: wrap; }
    .flag { display: inline-flex; align-items: center; height: 22px; padding: 0 8px; border-radius: 7px; font-size: 11.5px; font-weight: 750; text-transform: capitalize; background: var(--fr-surface); }
    .qty { position: absolute; top: 10px; inset-inline-end: 10px; min-width: 24px; height: 24px; padding: 0 7px; border-radius: 12px; background: var(--brand); color: #fff;
      font-size: 12.5px; font-weight: 750; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 8px rgba(0,0,0,0.2); }
    .body { padding: 15px 17px 17px; display: flex; flex-direction: column; flex: 1; }
    .name { font-weight: 750; font-size: 16.5px; color: var(--fr-text); letter-spacing: -0.01em; line-height: 1.25; }
    .desc { margin: 7px 0 0; font-size: 13.5px; color: var(--fr-text-3); line-height: 1.5; flex: 1;
      display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
    /* always the last row of the card, so price + Add line up across a grid row */
    .foot { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-top: auto; padding-top: 14px; flex-wrap: nowrap; }
    .add { height: 38px; padding: 0 15px; border-radius: 11px; border: none; cursor: pointer; background: var(--brand); color: #fff;
      font-family: var(--fr-font); font-weight: 750; font-size: 14px; display: flex; align-items: center; gap: 6px; flex-shrink: 0; white-space: nowrap; }
  `],
  template: `<div class="card" (click)="open.emit(item)">
      <div class="media">
        <img [src]="item.image || logo" [class.logo]="!item.image && !fallbackIsPhoto" alt="" onerror="this.style.visibility='hidden'">
        <div class="flags" *ngIf="item.flags?.length">
          <span *ngFor="let f of item.flags" class="flag" [style.color]="flagColor(f)">{{ flagLabel(f) }}</span>
        </div>
        <span *ngIf="qty > 0" class="qty fr-num">{{ qty }}</span>
      </div>
      <div class="body">
        <div class="name">{{ item.name }}</div>
        <p class="desc" *ngIf="item.description">{{ item.description }}</p>
        <div class="foot">
          <app-price [value]="item.price" [size]="17" [weight]="800"></app-price>
          <button class="add" (click)="$event.stopPropagation(); open.emit(item)">
            <app-icon name="solar:add-circle-bold" [size]="16"></app-icon>{{ 'Add' | t }}
          </button>
        </div>
      </div>
    </div>`,
})
export class DeskItemCardComponent {
  @Input() item!: CardItem;
  @Input() qty = 0;
  @Output() open = new EventEmitter<CardItem>();

  constructor(private sf: StorefrontService) {}

  /** Stand-in for items with no photo: the default item image, else the logo. */
  get logo(): string { return this.sf.brand?.itemImage || this.sf.brand?.logo || ''; }
  get fallbackIsPhoto(): boolean { return !!this.sf.brand?.itemImageIsPhoto; }
  flagLabel(key: string): string { return this.sf.flagDefs[key]?.label || key.replace(/_/g, ' '); }
  flagColor(key: string): string { return this.sf.flagDefs[key]?.color || 'var(--brand-deep)'; }
}
